import {
    ComponentPropsWithoutRef,
    ForwardedRef,
    PropsWithChildren,
    Suspense,
    forwardRef,
    useContext,
    useMemo,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useLocation } from 'react-router-dom';
import { Handle, Position, useStoreApi } from 'reactflow';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Button } from '@givebutter/primitives/button';
import { Loader } from '@givebutter/primitives/loader';
import { faEllipsisV, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { show as showIntercomChat } from '@intercom/messenger-js-sdk';
import {
    CanvasNodeContext,
    useCurrentNode,
} from 'assets/js/pages/engage/workflows/hooks/useCurrentNode';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Tooltip from 'components/tooltip';
import { twMerge } from 'tailwind-merge';

interface CanvasNodeRootProps extends ComponentPropsWithoutRef<'div'> {
    isSelected?: boolean;
    isError?: boolean;
    id: string;
}

const CanvasNodeRoot = forwardRef(
    (
        {
            children,
            className,
            isSelected = false,
            isError = false,
            id,
            ...restProps
        }: CanvasNodeRootProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const value = useMemo(() => ({ isSelected, isError, id }), [isSelected, isError, id]);
        return (
            <CanvasNodeContext.Provider value={value}>
                <div className={twMerge('relative', className)} ref={forwardedRef} {...restProps}>
                    <Handle
                        type="target"
                        position={Position.Top}
                        className="!top-1 opacity-0"
                        isConnectable={false}
                    />
                    {children}
                    <Handle
                        type="source"
                        position={Position.Bottom}
                        className="!bottom-1 opacity-0"
                        isConnectable={false}
                    />
                </div>
            </CanvasNodeContext.Provider>
        );
    },
);
CanvasNodeRoot.displayName = 'CanvasNodeRoot';

interface CanvasNodeContainerProps extends ComponentPropsWithoutRef<'div'> {}
const CanvasNodeContainer = forwardRef(
    (
        { className, ...restProps }: CanvasNodeContainerProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const { isSelected, isError } = useContext(CanvasNodeContext);
        return (
            <div
                className={twMerge(
                    'relative min-w-80 max-w-[calc(100vw_-_2rem)] rounded-xl border border-gray-200 bg-white shadow-sm transition-all md:min-w-96',
                    isSelected
                        ? 'border-gray-400 shadow-lg'
                        : 'cursor-pointer hover:border-gray-300 hover:shadow-md',
                    isError && '!border-red-600',
                    className,
                )}
                ref={forwardedRef}
                {...restProps}
            ></div>
        );
    },
);
CanvasNodeContainer.displayName = 'CanvasNodeContainer';

interface CanvasNodeHeaderProps extends ComponentPropsWithoutRef<'div'> {}
const CanvasNodeHeader = forwardRef(
    (
        { className, ...restProps }: CanvasNodeHeaderProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        return (
            <div
                className={twMerge('relative grid grid-cols-[24px_1fr] px-4 pt-4', className)}
                ref={forwardedRef}
                {...restProps}
            />
        );
    },
);
CanvasNodeHeader.displayName = 'CanvasNodeHeader';

interface CanvasNodeIconProps extends FontAwesomeIconProps {}
const CanvasNodeIcon = forwardRef(
    (
        { className, ...restProps }: CanvasNodeIconProps,
        forwardedRef: ForwardedRef<SVGSVGElement>,
    ) => {
        return (
            <FontAwesomeIcon
                ref={forwardedRef}
                className={twMerge('h-6 w-4', className)}
                {...restProps}
            />
        );
    },
);
CanvasNodeIcon.displayName = 'CanvasNodeIcon';

interface CanvasNodeTitleProps extends ComponentPropsWithoutRef<'p'> {}
const CanvasNodeTitle = forwardRef(
    (
        { className, ...restProps }: CanvasNodeTitleProps,
        forwardedRef: ForwardedRef<HTMLParagraphElement>,
    ) => {
        return (
            <p className={twMerge('font-medium', className)} ref={forwardedRef} {...restProps} />
        );
    },
);
CanvasNodeTitle.displayName = 'CanvasNodeTitle';

interface CanvasNodeActionTriggerProps extends ComponentPropsWithoutRef<'button'> {}
const CanvasNodeActionTrigger = forwardRef(
    (
        { className, onClick: onClickConsumer, ...restProps }: CanvasNodeActionTriggerProps,
        forwardedRef: ForwardedRef<HTMLButtonElement>,
    ) => {
        const location = useLocation();
        if (!location.pathname.includes('build')) return null;

        return (
            <DropdownMenu.Trigger asChild ref={forwardedRef} {...restProps}>
                <Button
                    variant="transparent"
                    size="sm"
                    shape="square"
                    color="neutral"
                    onClick={e => {
                        e.stopPropagation();
                        onClickConsumer?.(e);
                    }}
                    className={twMerge('!hover:bg-gray-100 absolute right-2 top-2', className)}
                    aria-label="Action Menu"
                >
                    <FontAwesomeIcon icon={faEllipsisV} size="sm" />
                </Button>
            </DropdownMenu.Trigger>
        );
    },
);
CanvasNodeActionTrigger.displayName = 'CanvasNodeActionTrigger';

interface CanvasNodeDescriptionProps extends ComponentPropsWithoutRef<'p'> {}
const CanvasNodeDescription = forwardRef(
    (
        { className, ...restProps }: CanvasNodeDescriptionProps,
        forwardedRef: ForwardedRef<HTMLParagraphElement>,
    ) => {
        return (
            <p
                className={twMerge('font-regular text-sm text-gray-500', className)}
                ref={forwardedRef}
                {...restProps}
            />
        );
    },
);
CanvasNodeDescription.displayName = 'CanvasNodeDescription';

interface CanvasNodeBodyProps extends ComponentPropsWithoutRef<'div'> {}
const CanvasNodeBody = forwardRef(
    (
        { className, children, ...restProps }: CanvasNodeBodyProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        return (
            <div className={twMerge('p-4', className)} ref={forwardedRef} {...restProps}>
                <CanvasNodeErrorBoundary>
                    <Suspense fallback={<Loader className="h-16" variant="static" />}>
                        {children}
                    </Suspense>
                </CanvasNodeErrorBoundary>
            </div>
        );
    },
);
CanvasNodeBody.displayName = 'CanvasNodeBody';

interface CanvasNodeBodySelectedProps extends ComponentPropsWithoutRef<'div'> {}
const CanvasNodeBodySelected = forwardRef(
    (
        { className, ...restProps }: CanvasNodeBodySelectedProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const { isSelected } = useContext(CanvasNodeContext);
        return (
            <div
                className={twMerge('hidden flex-col gap-4', isSelected && 'flex')}
                ref={forwardedRef}
                {...restProps}
            />
        );
    },
);
CanvasNodeBodySelected.displayName = 'CanvasNodeBodySelected';

interface CanvasNodeBodyUnselectedProps extends ComponentPropsWithoutRef<'div'> {}
const CanvasNodeBodyUnselected = forwardRef(
    (
        { className, ...restProps }: CanvasNodeBodyUnselectedProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const { isSelected } = useContext(CanvasNodeContext);
        return (
            <div className={twMerge(isSelected && 'hidden')} ref={forwardedRef} {...restProps} />
        );
    },
);
CanvasNodeBodyUnselected.displayName = 'CanvasNodeBodyUnselected';

interface CanvasNodeErrorAlertProps extends ComponentPropsWithoutRef<'div'> {
    isError?: boolean;
}
const CanvasNodeErrorAlert = forwardRef(
    (
        { className, children, isError, ...restProps }: CanvasNodeErrorAlertProps,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) => {
        const node = useCurrentNode();
        const formErrors = useMemo(
            () => node?.data?.formData?.errors || {},
            [node?.data?.formData?.errors],
        );
        const formErrorKeys = Object.keys(formErrors);
        const isVisible = isError || (!node?.selected && formErrorKeys.length > 0);

        const errorMessages = useMemo(() => {
            if (formErrorKeys.length === 0) return [];
            let messages: string[] = [];

            const findErrorMessages = (errors: Record<string, any>) => {
                for (const key in errors) {
                    if (errors[key]?.message && typeof errors[key].message === 'string') {
                        messages.push(errors[key].message);
                    } else {
                        findErrorMessages(errors[key]);
                    }
                }
            };

            findErrorMessages(formErrors);
            return messages;
        }, [formErrors, formErrorKeys]);

        return (
            isVisible && (
                <div
                    className={twMerge('absolute -right-8 top-4', className)}
                    {...restProps}
                    ref={forwardedRef}
                >
                    <Tooltip.Root>
                        <Tooltip.Trigger>
                            <span className="sr-only">Alert</span>
                            <FontAwesomeIcon
                                icon={faTriangleExclamation}
                                className="text-red-600"
                            />
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content side="right" align="center" inverse>
                                <Tooltip.Arrow inverse />
                                {children || (
                                    <ul className="flex flex-col gap-0.5 px-4">
                                        {errorMessages.map(message => (
                                            <li className="list-disc text-sm" key={message}>
                                                {message}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip.Root>
                </div>
            )
        );
    },
);
CanvasNodeErrorAlert.displayName = 'CanvasNodeErrorAlert';

interface CanvasNodeErrorBoundaryProps extends PropsWithChildren {}
const CanvasNodeErrorBoundary = ({ children }: CanvasNodeErrorBoundaryProps) => {
    const { getState } = useStoreApi();
    const isNodeSelectable = getState().elementsSelectable;
    return (
        <QueryErrorResetBoundary>
            {({ reset }) => (
                <ErrorBoundary
                    onReset={reset}
                    fallbackRender={({ resetErrorBoundary }) => (
                        <AlertNotification.Root type="error" className="max-w-96">
                            <AlertNotification.Icon />
                            <AlertNotification.Content className="gap-4">
                                <AlertNotification.Title>
                                    There was an error loading this step.
                                </AlertNotification.Title>
                                {isNodeSelectable && (
                                    <AlertNotification.Description className="flex gap-2">
                                        <Button
                                            size="sm"
                                            variant="solid"
                                            color="primary"
                                            onClick={() => resetErrorBoundary()}
                                        >
                                            Try again
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            color="neutral"
                                            onClick={() => showIntercomChat()}
                                        >
                                            Contact support
                                        </Button>
                                    </AlertNotification.Description>
                                )}
                            </AlertNotification.Content>
                        </AlertNotification.Root>
                    )}
                >
                    {children}
                </ErrorBoundary>
            )}
        </QueryErrorResetBoundary>
    );
};

const Root = CanvasNodeRoot;
const Container = CanvasNodeContainer;
const Header = CanvasNodeHeader;
const ActionMenuTrigger = CanvasNodeActionTrigger;
const Icon = CanvasNodeIcon;
const Title = CanvasNodeTitle;
const Description = CanvasNodeDescription;
const Body = CanvasNodeBody;
const BodySelected = CanvasNodeBodySelected;
const BodyUnselected = CanvasNodeBodyUnselected;
const ErrorAlert = CanvasNodeErrorAlert;

export {
    CanvasNodeRoot,
    CanvasNodeContainer,
    CanvasNodeHeader,
    CanvasNodeActionTrigger,
    CanvasNodeIcon,
    CanvasNodeTitle,
    CanvasNodeDescription,
    CanvasNodeBody,
    CanvasNodeBodySelected,
    CanvasNodeBodyUnselected,
    CanvasNodeErrorAlert,
    //
    Root,
    Container,
    Header,
    ActionMenuTrigger,
    Icon,
    Title,
    Description,
    Body,
    BodySelected,
    BodyUnselected,
    ErrorAlert,
};
export type {
    CanvasNodeRootProps,
    CanvasNodeContainerProps,
    CanvasNodeHeaderProps,
    CanvasNodeIconProps,
    CanvasNodeTitleProps,
    CanvasNodeActionTriggerProps,
    CanvasNodeDescriptionProps,
    CanvasNodeBodyProps,
    CanvasNodeErrorAlertProps,
};
